import React from 'react'
import { Platform, View } from 'react-native'
import Animated, {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
} from 'react-native-reanimated'

import useAnimatedPress from '@ui/animated/useAnimatedPress'
import Gradient from '@ui/effects/Gradient'
import { colors } from '@ui/colors'
import TileGlass from '@ui/tiles/TileGlass'
import Typography from '@ui/typography'

import useRWD from '@ui/theme/useRWD'
import cn from '@ui/utils/cn'
import { TileOptionProps } from './types'

import { leadingNone } from '@ui/typography/styles'
import RecommendedBadge from '@ui/elements/RecommendedBadge'
import { OutlineAnimated } from '@ui/elements/Outline'
import SafePressable from '@ui/buttons/SafePressable'

const borderRadius = 32

const border = {
  borderWidth: 1,
  borderColor: colors.black10,
  borderRadius,
}

const sTop = cn('right absolute left-0 right-0 flex items-center')

const onPressBackground = colors.gray[100]

export default function TileOption(props: TileOptionProps) {
  const {
    title,
    desc,
    label,
    image,
    cta,
    onPress,
    backgroundGradient,
    blurMode,
    magicGlow,
    isRecommended,
    debugContent,
  } = props
  const { tiles } = useRWD()
  const { animation, onPressIn, onPressOut } = useAnimatedPress()

  const showOnPress = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animation.value, [0, 1], [0, 1]),
    }
  })

  const hideOnPress = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animation.value, [0, 1], [1, 0]),
    }
  })

  // const stylePressedLabel = useAnimatedStyle(() => {
  //   return {
  //     backgroundColor: interpolateColor(
  //       animation.value,
  //       [0, 1],
  //       [colors.gray[75], onPressBackground]
  //     ),
  //   }
  // })

  const width = props.width || tiles.option.width
  const height = props.height || tiles.option.height
  const imageHeight = props.imageHeight || tiles.option.image.height

  const padding = Platform.select({
    default: 32,
    web: 40,
  })

  return (
    <SafePressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={{ width, height }}
    >
      <View
        className="relative"
        style={{
          width,
          height,
        }}
      >
        {isRecommended && (
          <OutlineAnimated
            size={4}
            borderRadius={borderRadius}
            color={colors.violet[600]}
          />
        )}
        <TileGlass
          animation={animation}
          onPressBackground={onPressBackground}
          magicGlow={magicGlow}
          className={cn(
            'overflow-hidden-fixer flex h-full w-full flex-col justify-between'
          )}
          style={[border]}
          image={image}
          imageProps={{
            contentPosition: 'center',
          }}
          imageWrapperStyle={{
            position: 'absolute',
            height: imageHeight,
            right: 0,
            left: 0,
            bottom: 'auto',
            top: '50%',
            transform: [{ translateY: -imageHeight / 2 }],
            justifyContent: 'center',
            alignItems: 'center',
          }}
          blur={50}
          blurMode={blurMode}
          blurClassName="bottom-0 left-0 right-0 top-0"
          blurStyle={{
            opacity: backgroundGradient ? 0 : 1,
            transform: [
              { translateY: height / 2 },
              { scale: Platform.select({ default: 1.25, web: 1.25 }) },
            ],
          }}
          gradient={
            <>
              <Gradient
                colors={Platform.select({
                  default: [colors.gray[75], colors.white50],
                  web: [colors.gray[75], colors.white50],
                })}
                start={[0.5, 0]}
                end={[0.5, 1]}
                style={[hideOnPress]}
              />
              <Gradient
                colors={Platform.select({
                  default: [colors.gray[200], colors.white0],
                  web: [colors.gray[200], colors.white0],
                })}
                start={[0.5, 0]}
                end={[0.5, 1]}
                style={[showOnPress]}
              />
            </>
          }
          underBlurComponent={
            <>
              {backgroundGradient && (
                <View
                  className="absolute"
                  style={{
                    width: width / 2,
                    height: width / 3,
                    left: width / 4,
                    bottom: 0,
                  }}
                >
                  <Gradient
                    colors={backgroundGradient}
                    start={[0.5, 0]}
                    end={[0.5, 1]}
                  />
                </View>
              )}
            </>
          }
        >
          {/* <Border /> */}
          <View style={{ padding }}>
            <View className="flex gap-3">
              <Typography
                variant="LGB"
                className={cn(
                  'text-center leading-tighter',
                  Platform.select({
                    default: 'tracking-[-1]',
                    web: 'tracking-[-0.05em]',
                  })
                )}
                numberOfLines={2}
              >
                {title}
              </Typography>
              <Typography
                variant={Platform.select({ default: 'SM', web: 'MD' })}
                className="text-center leading-tight color-desc"
                numberOfLines={3}
              >
                {desc}
              </Typography>
            </View>
          </View>
          <View className="absolute bottom-0 w-full items-center justify-center">
            <View style={{ paddingBottom: padding }}>{cta}</View>
          </View>
        </TileGlass>
        {label && (
          <View className={sTop}>
            <View className="relative">
              <Animated.View
                className={cn('h-8 bg-gray-75 px-6')}
                style={[
                  // stylePressedLabel,
                  border,
                  { borderRadius: 48, top: -16 },
                ]}
              >
                <Typography
                  variant="XSB"
                  className="uppercase leading-none tracking-[0.6] color-desc"
                  style={{ opacity: 0 }}
                >
                  {label}
                </Typography>
                {/* <Animated.View
                className="bg-gray-75 absolute"
                style={[
                  // stylePressedLabel,
                  { bottom: -1, width: width / 2, height: 15, left: -1 },
                ]}
              /> */}
              </Animated.View>
              <View
                className={cn(
                  'h-8 px-6 py-2',
                  'flex items-center justify-center'
                )}
                style={[{ top: -16, position: 'absolute' }]}
              >
                <Typography
                  variant="XSB"
                  className={cn(
                    'uppercase tracking-[0.6] color-desc',
                    leadingNone
                  )}
                >
                  {label}
                </Typography>
              </View>
            </View>
          </View>
        )}
        {debugContent}
        {isRecommended && <RecommendedBadge />}
      </View>
    </SafePressable>
  )
}
